import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { fetchBranches } from "../../../api/branches";
import UserLink from "../../../components/common/UserLink";
import {
    fetchPaymentsDueReport,
    exportPaymentsDueReport,
    emailPaymentsDueReport,
} from "../../../api/adminReports";
import ReportExportBar from "../../../components/reports/ReportExportBar";
import ExcelDownloadButton from "../../../components/common/ExcelDownloadButton";
import { exportToExcel } from "../../../utils/excelExport";
import ReportGenerateButton from "../../../components/reports/ReportGenerateButton";
import { ListLoadingSpinner } from "../../../components/layout/ListPageLayout";
import { formatCurrency as fmt } from "../../../utils/currency";
import { TextInput, SelectInput } from "../../../components/common/form";
import PaginationFooter from "../../../components/common/PaginationFooter";
import SortableTableHeader from "../../../components/common/SortableTableHeader";
import useSortedClientPagination from "../../../hooks/useSortedClientPagination";
import { paymentsDueSortColumns } from "../../../config/tableSortColumns";
import {
    ChartPanel,
    CompareBar,
    CompositionDonut,
    CHART_COLORS,
} from "../../../components/charts";
import { apiErrorMessage } from '../../../utils/apiError';

// ─── Download helper ──────────────────────────────────────────────────────────
async function triggerDownload(blobReq, filename) {
    const res = await blobReq;
    const blob = new Blob([res.data], {
        type: res.headers["content-type"] || "application/octet-stream",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
}

// ─── Status badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
    const { t } = useTranslation();
    if (status === "OVERDUE") {
        return (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-red-100 text-red-800 border border-red-200">
                {t("reports.common.overdue")}
            </span>
        );
    }
    return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-amber-100 text-amber-800 border border-amber-200">
            {t("reports.common.lowBalance")}
        </span>
    );
}

// ─── KPI card ─────────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, colorClass = "text-slate-900" }) {
    return (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col gap-1">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</div>
            <div className={`text-2xl font-bold ${colorClass}`}>{value}</div>
            {sub && <div className="text-xs text-gray-500">{sub}</div>}
        </div>
    );
}

export default function PaymentsDueReport() {
    const { t } = useTranslation();
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [branchId, setBranchId] = useState("");
    const [thresholdInput, setThresholdInput] = useState("");

    const [branches, setBranches] = useState([]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [hasRun, setHasRun] = useState(false);
    const [filtersLoadError, setFiltersLoadError] = useState(false);

    useEffect(() => {
        fetchBranches()
            .then((res) => {
                setBranches(res?.data || []);
                setFiltersLoadError(false);
            })
            .catch(() => {
                setBranches([]);
                setFiltersLoadError(true);
            });
    }, []);

    const generate = async () => {
        setErr("");
        setLoading(true);
        setHasRun(true);
        try {
            const res = await fetchPaymentsDueReport({
                statusFilter: statusFilter !== "ALL" ? statusFilter : undefined,
                thresholdOverride: thresholdInput !== "" ? Number(thresholdInput) : undefined,
                branchId: branchId || undefined,
            });
            setData(res.data);
        } catch (e) {
            setErr(apiErrorMessage(e, t("reports.common.failedToLoad")));
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    // ── Export params ─────────────────────────────────────────────────────────
    const exportParams = {
        statusFilter: statusFilter !== "ALL" ? statusFilter : undefined,
        thresholdOverride: thresholdInput !== "" ? Number(thresholdInput) : undefined,
        branchId: branchId || undefined,
    };

    const handleExportPdf = () =>
        triggerDownload(
            exportPaymentsDueReport({ ...exportParams, format: "pdf" }),
            "payments-due.pdf"
        );

    const handleExportCsv = () =>
        triggerDownload(
            exportPaymentsDueReport({ ...exportParams, format: "csv" }),
            "payments-due.csv"
        );

    const handleSendEmail = (email) =>
        emailPaymentsDueReport({ ...exportParams, format: "pdf", toEmail: email });

    const s = data?.summary;
    const rows = data?.rows || [];
    const rowsPagination = useSortedClientPagination(rows, {
        columns: paymentsDueSortColumns,
        resetKey: data?.generatedAt ?? rows.length,
    });
    const totalAffected = s ? s.lowBalanceCount : 0;
    const affectedPct = s && s.totalInScope > 0
        ? ((totalAffected / s.totalInScope) * 100).toFixed(1)
        : null;

    const statusBucketData = useMemo(() => {
        if (!s) return [];
        const overdue = Number(s.overdueCount) || 0;
        const lowOnly = Math.max(0, (Number(s.lowBalanceCount) || 0) - overdue);
        const ok = Math.max(0, (Number(s.totalInScope) || 0) - (Number(s.lowBalanceCount) || 0));
        return [
            {
                name: t("reports.common.overdue", { defaultValue: "Overdue" }),
                value: overdue,
                color: CHART_COLORS.danger,
            },
            {
                name: t("reports.common.lowBalance", { defaultValue: "Low balance" }),
                value: lowOnly,
                color: CHART_COLORS.warning,
            },
            {
                name: t("reports.paymentsDue.statusOk", { defaultValue: "OK" }),
                value: ok,
                color: CHART_COLORS.success,
            },
        ].filter((d) => d.value > 0);
    }, [s, t]);

    const branchAmountData = useMemo(() => {
        if (!rows.length) return [];
        const byBranch = {};
        for (const r of rows) {
            const key = r.branchName || t("reports.common.unknown", { defaultValue: "Unknown" });
            if (!byBranch[key]) byBranch[key] = 0;
            byBranch[key] += Number(r.amountDue) || 0;
        }
        return Object.entries(byBranch)
            .map(([name, value]) => ({ name, value }))
            .filter((d) => d.value > 0)
            .sort((a, b) => b.value - a.value);
    }, [rows, t]);

    return (
        <div className="p-6 space-y-6">
            <p className="text-sm text-gray-600">
                {t("reports.paymentsDue.subtitle")}
            </p>

            {/* ── Filters ──────────────────────────────────────────────────── */}
            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-800 flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                    {t("reports.common.filters")}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                    <SelectInput
                        label={t("reports.paymentsDue.paymentStatus")}
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="ALL">{t("reports.paymentsDue.statusAll")}</option>
                        <option value="OVERDUE">{t("reports.paymentsDue.statusOverdue")}</option>
                        <option value="LOW_BALANCE">{t("reports.paymentsDue.statusLowBalance")}</option>
                    </SelectInput>

                    <SelectInput
                        label={t("reports.common.filterByBranch")}
                        disabled={filtersLoadError}
                        value={branchId}
                        onChange={(e) => setBranchId(e.target.value)}
                    >
                        <option value="">{t("reports.common.allBranches")}</option>
                        {branches.map((b) => (
                            <option key={b.branchId ?? b.id} value={b.branchId ?? b.id}>
                                {b.name}
                            </option>
                        ))}
                    </SelectInput>

                    <TextInput
                        label={t("reports.paymentsDue.thresholdOverride")}
                        type="number"
                        min="0"
                        step="any"
                        value={thresholdInput}
                        onChange={(e) => setThresholdInput(e.target.value)}
                        placeholder={t("reports.paymentsDue.thresholdPlaceholder")}
                    />

                    <ReportGenerateButton
                        onClick={generate}
                        loading={loading}
                        label={t("reports.common.generateReport")}
                        loadingLabel={t("reports.common.loadingReport")}
                    />
                </div>

                {hasRun && !loading && data && (
                    <p className="mt-3 text-sm text-gray-500">
                        {t("reports.common.studentsFound", { count: rows.length })}
                        {s && ` · ${t("reports.paymentsDue.thresholdHint", { amount: fmt(s.thresholdUsed) })}`}
                    </p>
                )}

                {filtersLoadError && (
                    <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="status">{t("common.optionsLoadFailed")}</div>
                )}
                {err && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                        {err}
                    </div>
                )}
            </div>

            {loading && (
                <ListLoadingSpinner />
            )}

            {hasRun && (
                <div className={`grid grid-cols-1 ${branchAmountData.length > 0 || loading ? "lg:grid-cols-2" : ""} gap-4`}>
                    <ChartPanel
                        title={t("charts.paymentsDueStatus", { defaultValue: "Payment status mix" })}
                        subtitle={t("charts.paymentsDueStatusHint", { defaultValue: "Overdue, low balance, and OK students" })}
                        loading={loading}
                        empty={!loading && statusBucketData.length === 0}
                        emptyMessage={t("charts.noStatusData", { defaultValue: "No status buckets to chart" })}
                        error={err || null}
                        height={240}
                    >
                        <CompositionDonut data={statusBucketData} chartSize={180} />
                    </ChartPanel>
                    {(loading || branchAmountData.length > 0) && (
                        <ChartPanel
                            title={t("charts.paymentsDueByBranch", { defaultValue: "Amount due by branch" })}
                            subtitle={t("charts.paymentsDueByBranchHint", { defaultValue: "Overdue amounts aggregated by branch" })}
                            loading={loading}
                            empty={!loading && branchAmountData.length === 0}
                            emptyMessage={t("charts.noBranchAmountData", { defaultValue: "No branch amounts to chart" })}
                            error={err || null}
                            height={Math.max(240, 48 + branchAmountData.length * 36)}
                        >
                            <CompareBar
                                data={branchAmountData}
                                layout="horizontal"
                                height={Math.max(220, branchAmountData.length * 36)}
                                yFormatter={(v) => fmt(v)}
                            />
                        </ChartPanel>
                    )}
                </div>
            )}

            {!loading && hasRun && data && (
                <>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <KpiCard
                            label={t("reports.paymentsDue.overdueStudents")}
                            value={s.overdueCount}
                            sub={t("reports.paymentsDue.overdueSub")}
                            colorClass="text-red-600"
                        />
                        <KpiCard
                            label={t("reports.paymentsDue.totalAmountDue")}
                            value={fmt(s.totalAmountDue)}
                            sub={t("reports.paymentsDue.totalDueSub")}
                            colorClass="text-red-600"
                        />
                        <KpiCard
                            label={t("reports.paymentsDue.belowThreshold")}
                            value={s.lowBalanceCount}
                            sub={`${t("reports.common.balance")} ≤ ${fmt(s.thresholdUsed)}`}
                            colorClass="text-amber-600"
                        />
                        <KpiCard
                            label={t("reports.paymentsDue.studentsInScope")}
                            value={s.totalInScope}
                            sub={affectedPct != null ? t("reports.paymentsDue.belowSub", { pct: affectedPct }) : undefined}
                            colorClass="text-slate-900"
                        />
                    </div>

                    {rows.length > 0 && (
                        <div className="flex flex-wrap items-center gap-3">
                            <ExcelDownloadButton
                                onExport={() => exportToExcel([
                                    { header: t("reports.common.student"), key: "studentName", width: 24 },
                                    { header: t("reports.common.studentId"), key: "studentId", width: 10 },
                                    {
                                        header: t("reports.common.status"),
                                        key: "status",
                                        width: 14,
                                        render: (v) => (v === "OVERDUE"
                                            ? t("reports.common.overdue")
                                            : t("reports.common.lowBalance")),
                                    },
                                    { header: t("reports.common.branch"), key: "branchName", width: 18 },
                                    { header: t("reports.common.batchesCol"), key: "batches", width: 24 },
                                    { header: t("reports.common.balance"), key: "balance", render: (v) => fmt(v || 0), width: 12 },
                                    { header: t("reports.common.amountDue"), key: "amountDue", render: (v) => fmt(v || 0), width: 12 },
                                    { header: t("reports.common.lastTopup"), key: "lastTopupDate", width: 16 },
                                    { header: t("reports.common.amount"), key: "lastTopupAmount", render: (v) => v != null ? fmt(v) : "", width: 16 },
                                    { header: t("reports.paymentsDue.sessionsSince"), key: "sessionsSinceLastTopup", width: 14 },
                                    { header: t("reports.paymentsDue.ratePerSession"), key: "perSessionRate", render: (v) => v != null ? fmt(v) : "", width: 12 },
                                    { header: t("reports.common.phone"), key: "phone", width: 16 },
                                    { header: t("reports.common.email"), key: "email", width: 28 },
                                ], rows, "payments-due")}
                                filename="payments-due"
                                disabled={rows.length === 0}
                            />
                            <ReportExportBar
                                onExportPdf={handleExportPdf}
                                onExportCsv={handleExportCsv}
                                onSendEmail={handleSendEmail}
                                emailLabel={t("reports.common.enterEmail")}
                            />
                        </div>
                    )}

                    {rows.length === 0 ? (
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-10 text-center text-gray-500 text-sm">
                            {t("reports.paymentsDue.empty")}
                        </div>
                    ) : (
                        <>
                        <div className="md:hidden space-y-3">
                            {rowsPagination.pageItems.map((r) => {
                                const isOverdue = r.status === "OVERDUE";
                                return (
                                    <article
                                        key={r.studentId}
                                        className={`rounded-xl border p-3.5 shadow-sm ${isOverdue ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50"}`}
                                    >
                                        <div className="flex items-start justify-between gap-2 mb-3">
                                            <div className="min-w-0">
                                                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{t("reports.common.student")}</p>
                                                <UserLink id={r.studentId} name={r.studentName} className="font-semibold text-gray-900" />
                                                <p className="text-xs text-gray-500">#{r.studentId}</p>
                                            </div>
                                            <StatusBadge status={r.status} />
                                        </div>
                                        <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm border-t border-gray-200/70 pt-3">
                                            <div className="min-w-0">
                                                <dt className="text-[11px] uppercase tracking-wide text-gray-400">{t("reports.common.branch")}</dt>
                                                <dd className="font-medium text-gray-800 truncate">{r.branchName || "—"}</dd>
                                            </div>
                                            <div className="min-w-0">
                                                <dt className="text-[11px] uppercase tracking-wide text-gray-400">{t("reports.common.balance")}</dt>
                                                <dd className={`font-bold tabular-nums ${isOverdue ? "text-red-700" : "text-amber-700"}`}>{fmt(r.balance)}</dd>
                                            </div>
                                            <div className="min-w-0 col-span-2">
                                                <dt className="text-[11px] uppercase tracking-wide text-gray-400">{t("reports.common.batchesCol")}</dt>
                                                <dd className="text-xs text-gray-700">{r.batches || "—"}</dd>
                                            </div>
                                            <div className="min-w-0">
                                                <dt className="text-[11px] uppercase tracking-wide text-gray-400">{t("reports.common.amountDue")}</dt>
                                                <dd className="font-bold text-red-700 tabular-nums">{isOverdue ? fmt(r.amountDue) : "—"}</dd>
                                            </div>
                                            <div className="min-w-0">
                                                <dt className="text-[11px] uppercase tracking-wide text-gray-400">{t("reports.common.lastTopup")}</dt>
                                                <dd className="text-gray-700 text-xs">
                                                    {r.lastTopupDate ? (
                                                        <>
                                                            <div>{r.lastTopupDate || '—'}</div>
                                                            {r.lastTopupAmount != null && <div className="text-gray-500">{fmt(r.lastTopupAmount)}</div>}
                                                        </>
                                                    ) : t("reports.common.never")}
                                                </dd>
                                            </div>
                                            <div className="min-w-0">
                                                <dt className="text-[11px] uppercase tracking-wide text-gray-400">{t("reports.common.phone")}</dt>
                                                <dd className="text-xs text-gray-700">{r.phone || "—"}</dd>
                                            </div>
                                            <div className="min-w-0 col-span-2">
                                                <dt className="text-[11px] uppercase tracking-wide text-gray-400">{t("reports.common.email")}</dt>
                                                <dd className="text-xs text-gray-700 break-all">{r.email || "—"}</dd>
                                            </div>
                                        </dl>
                                    </article>
                                );
                            })}
                        </div>

                        <div className="hidden md:block bg-white rounded-xl border border-gray-200 shadow-sm overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-gray-200">
                                        <SortableTableHeader label={t("reports.common.student")} columnKey="student" sortKey={rowsPagination.sortKey} sortDir={rowsPagination.sortDir} onSort={rowsPagination.toggleSort} className="text-left px-4 py-3 font-semibold text-gray-700 whitespace-nowrap" align="left" />
                                        <SortableTableHeader label={t("reports.common.status")} columnKey="status" sortKey={rowsPagination.sortKey} sortDir={rowsPagination.sortDir} onSort={rowsPagination.toggleSort} className="text-left px-4 py-3 font-semibold text-gray-700 whitespace-nowrap" align="left" />
                                        <SortableTableHeader label={t("reports.common.branch")} columnKey="branch" sortKey={rowsPagination.sortKey} sortDir={rowsPagination.sortDir} onSort={rowsPagination.toggleSort} className="text-left px-4 py-3 font-semibold text-gray-700 whitespace-nowrap" align="left" />
                                        <th className="text-left px-4 py-3 font-semibold text-gray-700 whitespace-nowrap">{t("reports.common.batchesCol")}</th>
                                        <SortableTableHeader label={t("reports.common.balance")} columnKey="balance" sortKey={rowsPagination.sortKey} sortDir={rowsPagination.sortDir} onSort={rowsPagination.toggleSort} className="text-right px-4 py-3 font-semibold text-gray-700 whitespace-nowrap" align="right" />
                                        <th className="text-right px-4 py-3 font-semibold text-gray-700 whitespace-nowrap">{t("reports.common.amountDue")}</th>
                                        <SortableTableHeader label={t("reports.common.lastTopup")} columnKey="lastPayment" sortKey={rowsPagination.sortKey} sortDir={rowsPagination.sortDir} onSort={rowsPagination.toggleSort} className="text-left px-4 py-3 font-semibold text-gray-700 whitespace-nowrap" align="left" />
                                        <th className="text-right px-4 py-3 font-semibold text-gray-700 whitespace-nowrap">{t("reports.paymentsDue.sessionsSince")}</th>
                                        <th className="text-right px-4 py-3 font-semibold text-gray-700 whitespace-nowrap">{t("reports.paymentsDue.ratePerSession")}</th>
                                        <th className="text-left px-4 py-3 font-semibold text-gray-700 whitespace-nowrap">{t("reports.common.phone")}</th>
                                        <th className="text-left px-4 py-3 font-semibold text-gray-700 whitespace-nowrap">{t("reports.common.email")}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {rowsPagination.pageItems.map((r) => {
                                        const isOverdue = r.status === "OVERDUE";
                                        return (
                                            <tr
                                                key={r.studentId}
                                                className={isOverdue ? "bg-red-50 hover:bg-red-100" : "bg-amber-50 hover:bg-amber-100"}
                                            >
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <UserLink id={r.studentId} name={r.studentName} className="font-medium text-blue-600 hover:text-blue-800" />
                                                    <div className="text-xs text-gray-400">#{r.studentId}</div>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <StatusBadge status={r.status} />
                                                </td>
                                                <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{r.branchName || "—"}</td>
                                                <td className="px-4 py-3 text-gray-700 max-w-[180px]">
                                                    <span className="text-xs">{r.batches}</span>
                                                </td>
                                                <td className={`px-4 py-3 text-right font-bold whitespace-nowrap ${isOverdue ? "text-red-700" : "text-amber-700"}`}>
                                                    {fmt(r.balance)}
                                                </td>
                                                <td className="px-4 py-3 text-right font-bold whitespace-nowrap text-red-700">
                                                    {isOverdue ? fmt(r.amountDue) : "—"}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                                                    {r.lastTopupDate
                                                        ? (
                                                            <div>
                                                                <div>{r.lastTopupDate || '—'}</div>
                                                                {r.lastTopupAmount != null && (
                                                                    <div className="text-xs text-gray-500">{fmt(r.lastTopupAmount)}</div>
                                                                )}
                                                            </div>
                                                        )
                                                        : <span className="text-gray-400 italic text-xs">{t("reports.common.never")}</span>
                                                    }
                                                </td>
                                                <td className="px-4 py-3 text-right text-gray-700 whitespace-nowrap">
                                                    {r.sessionsSinceLastTopup}
                                                </td>
                                                <td className="px-4 py-3 text-right text-gray-700 whitespace-nowrap">
                                                    {r.perSessionRate != null ? fmt(r.perSessionRate) : "—"}
                                                </td>
                                                <td className="px-4 py-3 text-gray-700 whitespace-nowrap text-xs">
                                                    {r.phone || "—"}
                                                </td>
                                                <td className="px-4 py-3 text-gray-700 text-xs">
                                                    {r.email || "—"}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {rowsPagination.total > 0 && (
                            <PaginationFooter
                                page={rowsPagination.page}
                                size={rowsPagination.size}
                                total={rowsPagination.total}
                                onPageChange={rowsPagination.setPage}
                                onSizeChange={rowsPagination.setSize}
                            />
                        )}
                        </>
                    )}
                </>
            )}
        </div>
    );
}
